import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { CdsChatbot } from '@claycoappdev/chatbot';
import '@claycoappdev/chatbot/dist/styles.css';

/**
 * Wraps CdsChatbot and injects a Bearer token from MSAL
 * using the same token-acquisition pattern as ApiProvider / home.jsx.
 * Only renders if the user has the "Tester" or "tester" app role.
 * Loads the endpoint from window.env.baseUrl (settings.json).
 */
export default function AuthenticatedChatbot(props) {
    const { instance, accounts } = useMsal();
    const [token, setToken] = useState(null);
    const [endpoint, setEndpoint] = useState(null);

    // Check if user has the Tester role
    const hasTesterRole = accounts?.[0]?.idTokenClaims?.roles?.some(
        role => role.toLowerCase() === 'tester'
    );

    const loadSettings = useCallback(async () => {
        if (!window.env) {
            try {
                const resp = await fetch('/settings.json', {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                });
                window.env = await resp.json();
            } catch (err) {
                console.error('Failed to load settings.json', err);
            }
        }
        
        // Set endpoint from window.env.baseUrl + /chat
        if (window.env?.baseUrl) {
            setEndpoint(`${window.env.baseUrl}/chat`);
        }
    }, []);

    const acquireToken = useCallback(async () => {
        if (!accounts || accounts.length === 0) return null;

        await loadSettings();

        const request = {
            scopes: window.env?.apiScope ? [window.env.apiScope] : ['profile', 'User.Read'],
            account: accounts[0],
        };

        try {
            return (await instance.acquireTokenSilent(request)).accessToken;
        } catch {
            try {
                return (await instance.acquireTokenPopup(request)).accessToken;
            } catch {
                return null;
            }
        }
    }, [instance, accounts, loadSettings]);

    useEffect(() => {
        if (hasTesterRole) {
            acquireToken().then(setToken);
        }
    }, [acquireToken, hasTesterRole]);

    // Refresh the token every 5 minutes so the header stays valid
    useEffect(() => {
        if (!hasTesterRole) return;
        
        const id = setInterval(() => {
            acquireToken().then(setToken);
        }, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [acquireToken, hasTesterRole]);

    // Don't render if user doesn't have Tester role
    if (!hasTesterRole) return null;
    
    if (!token || !endpoint) return null;

    return (
        <CdsChatbot
            {...props}
            endpoint={endpoint}
            headers={{ ...props.headers, Authorization: `Bearer ${token}` }}
        />
    );
}
